import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const DEBOUNCE_MS = 300;

const GlobalSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  }), []);

  const runSearch = useCallback(async (q) => {
    if (q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API_BASE}/search-all`, {
        params: { q, limit: 30 },
        headers: authHeaders(),
      });
      setResults(res.data?.results || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setOpen(true);
    setLoading(value.trim().length >= 2);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), DEBOUNCE_MS);
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const handleResultClick = (r) => {
    setOpen(false);
    navigate(`/dashboard/${r.table_name}/form_view/${r.id}`);
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const grouped = results.reduce((acc, r) => {
    (acc[r.table_label] = acc[r.table_label] || []).push(r);
    return acc;
  }, {});

  return (
    <div ref={containerRef} style={styles.container}>
      <div style={styles.inputWrap}>
        <Search size={15} color="rgba(229,231,235,.55)" />
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => query.trim().length >= 2 && setOpen(true)}
          placeholder="Search everything..."
          style={styles.input}
        />
        {query && (
          <button type="button" onClick={clearSearch} style={styles.clearBtn} aria-label="Clear search">
            <X size={14} color="rgba(229,231,235,.55)" />
          </button>
        )}
      </div>

      {open && query.trim().length >= 2 && (
        <div style={styles.dropdown}>
          {loading ? (
            <div style={styles.empty}>Searching...</div>
          ) : results.length === 0 ? (
            <div style={styles.empty}>No matches.</div>
          ) : (
            Object.entries(grouped).map(([tableLabel, items]) => (
              <div key={tableLabel}>
                <div style={styles.groupHeader}>{tableLabel}</div>
                {items.map((r) => (
                  <div key={`${r.table_name}-${r.id}`} onClick={() => handleResultClick(r)} style={styles.item}>
                    <div style={styles.itemTitle}>{r.display_value || r.id}</div>
                    <div style={styles.itemMeta}>matched on {r.matched_field}</div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { position: "relative", flex: "0 1 320px", minWidth: 180 },
  inputWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
  },
  input: {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#e5e7eb",
    fontSize: 13,
  },
  clearBtn: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    padding: 0,
  },
  dropdown: {
    position: "absolute",
    top: 42,
    left: 0,
    width: 340,
    maxHeight: 420,
    overflowY: "auto",
    background: "#161d2e",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 10,
    boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
    zIndex: 1100,
  },
  empty: { padding: "16px 14px", fontSize: 13, color: "rgba(229,231,235,.5)" },
  groupHeader: {
    padding: "8px 14px 4px",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: ".04em",
    textTransform: "uppercase",
    color: "rgba(229,231,235,.45)",
  },
  item: {
    padding: "8px 14px",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    cursor: "pointer",
  },
  itemTitle: { fontSize: 13, fontWeight: 700, color: "#e5e7eb" },
  itemMeta: { fontSize: 11, color: "rgba(229,231,235,.45)", marginTop: 2 },
};

export default GlobalSearch;
